import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Sparkles, Gift, PartyPopper, Copy, Check, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const COUPON_CODE = "YENIYIL2026";
const END_DATE = new Date("2026-01-05T00:00:00");

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const NewYearPopup = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const calculateTimeLeft = (): TimeLeft => {
    const now = new Date();
    const difference = END_DATE.getTime() - now.getTime();

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  useEffect(() => {
    // Update countdown every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      toast.success("Kupon kodu kopyalandı!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopyalama başarısız oldu");
    }
  };

  const goToPricing = () => {
    setIsOpen(false);
    // Store coupon code in sessionStorage for auto-apply
    sessionStorage.setItem("auto-apply-coupon", COUPON_CODE);
    navigate("/pricing");
  };

  useEffect(() => {
    const now = new Date();
    
    if (now >= END_DATE) return;

    // Check if already shown in this session
    const shown = sessionStorage.getItem("newyear-popup-shown");
    if (shown) return;

    // Show popup after 2 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
      setHasShown(true);
      sessionStorage.setItem("newyear-popup-shown", "true");
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (!hasShown && !isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-md max-w-[92vw] p-0 overflow-hidden border-0 bg-transparent shadow-none">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative rounded-xl sm:rounded-2xl overflow-hidden"
            >
              {/* Background with gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-destructive opacity-95" />
              
              {/* Decorative elements - fewer on mobile */}
              <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                {Array.from({ length: 12 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-yellow-300 rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0, 1.5, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: Math.random() * 2,
                    }}
                  />
                ))}
              </div>

              {/* Content */}
              <div className="relative z-10 p-4 sm:p-6 text-center text-white">
                {/* Close button */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>

                {/* Icons - smaller on mobile */}
                <div className="flex justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <motion.div
                    animate={{ rotate: [-10, 10, -10] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    <PartyPopper className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-300" />
                  </motion.div>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-yellow-200" />
                  </motion.div>
                  <motion.div
                    animate={{ rotate: [10, -10, 10] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    <PartyPopper className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-300 scale-x-[-1]" />
                  </motion.div>
                </div>

                {/* Year */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="text-4xl sm:text-5xl font-bold mb-1 bg-gradient-to-r from-yellow-200 via-yellow-300 to-yellow-200 bg-clip-text text-transparent">
                    2026
                  </h2>
                </motion.div>

                {/* Title */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">
                    Yeni Yılınız Kutlu Olsun! 🎉
                  </h3>
                </motion.div>

                {/* Message */}
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-white/90 mb-4 text-sm sm:text-base leading-relaxed"
                >
                  2026 yılında işletmeniz için daha fazla başarı, 
                  daha fazla satış ve muhteşem videolar diliyoruz!
                </motion.p>

                {/* Special offer */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 mb-3 border border-white/20"
                >
                  <div className="flex items-center justify-center gap-1.5 mb-1.5">
                    <Gift className="h-4 w-4 text-yellow-300" />
                    <span className="font-semibold text-sm text-yellow-200">Yılbaşı Özel Fırsatı</span>
                  </div>
                  <p className="text-xs sm:text-sm text-white/80 mb-2">
                    Yeni yıla özel <span className="font-bold text-yellow-300">%20 indirim</span> ile 
                    video paketlerinizi yenileyin!
                  </p>
                  {/* Coupon Code Display */}
                  <div className="flex items-center justify-center gap-2">
                    <div className="bg-white/20 px-3 py-1.5 rounded-md border-2 border-dashed border-yellow-300/50">
                      <span className="font-mono font-bold text-sm sm:text-base text-yellow-200 tracking-wider">
                        {COUPON_CODE}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={copyCode}
                      className="text-white hover:bg-white/20 hover:text-white h-8 w-8 p-0"
                    >
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-green-400" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </motion.div>

                {/* Countdown Timer */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.55 }}
                  className="mb-4"
                >
                  <div className="flex items-center justify-center gap-1 mb-1.5">
                    <Clock className="h-3 w-3 text-yellow-300" />
                    <span className="text-xs text-yellow-200">Kampanya bitimine kalan süre</span>
                  </div>
                  <div className="flex justify-center gap-1.5 sm:gap-2">
                    <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 sm:px-3 py-1.5 min-w-[44px] sm:min-w-[52px]">
                      <div className="text-lg sm:text-xl font-bold text-yellow-200">{timeLeft.days}</div>
                      <div className="text-xs sm:text-xs text-white/60">Gün</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 sm:px-3 py-1.5 min-w-[44px] sm:min-w-[52px]">
                      <div className="text-lg sm:text-xl font-bold text-yellow-200">{String(timeLeft.hours).padStart(2, '0')}</div>
                      <div className="text-xs sm:text-xs text-white/60">Saat</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 sm:px-3 py-1.5 min-w-[44px] sm:min-w-[52px]">
                      <div className="text-lg sm:text-xl font-bold text-yellow-200">{String(timeLeft.minutes).padStart(2, '0')}</div>
                      <div className="text-xs sm:text-xs text-white/60">Dakika</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 sm:px-3 py-1.5 min-w-[44px] sm:min-w-[52px]">
                      <div className="text-lg sm:text-xl font-bold text-yellow-200">{String(timeLeft.seconds).padStart(2, '0')}</div>
                      <div className="text-xs sm:text-xs text-white/60">Saniye</div>
                    </div>
                  </div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="flex flex-col gap-2"
                >
                  <Button
                    onClick={goToPricing}
                    className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold px-4 h-9 sm:h-10 text-sm"
                  >
                    <Gift className="h-4 w-4 mr-2" />
                    Fırsatı Yakala
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsOpen(false)}
                    className="border-white/30 text-white hover:bg-white/10 hover:text-white h-9 sm:h-10 text-sm"
                  >
                    Daha Sonra
                  </Button>
                </motion.div>

                {/* Footer text */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="text-xs sm:text-xs text-white/50 mt-3 sm:mt-4"
                >
                  🎊 ReelMaker Ekibi size mutlu bir yıl diler! 🎊
                </motion.p>
              </div>
            </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default NewYearPopup;
